import { api } from 'api';
import CustomCalculatorFormAccordion from 'components/Transactions/components/CustomCalculatorFormAccordion';
import TooltipCommon from 'components/common/TooltipCommon';
import TooltipCommonMobile from 'components/common/TooltipCommon/mobile';
import {
  ADD_TRANSACTION_PAYMENT_METHOD_NAME,
  ADD_TRANSACTION_PAYOUT_METHOD_NAME,
  ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME,
  ADD_TRANSACTION_YOU_SEND_NAME,
  DESKTOP_MIN_WIDTH,
  SENDING_AMOUNT_DISCOUNT_PERCENTS,
} from 'constants';
import CalculatorRoundingAnimationLottie from 'features/calculatorRounding/components/CalculatorRoundingAnimationLottie';
import useCalculatorRoundingStore from 'features/calculatorRounding/hooks/useCalculatorRoundingStore';
import { format } from 'helpers';
import { checkMaintenance } from 'helpers/maintenance';
import useAddTransaction from 'hooks/form-add-transaction/useAddTransaction';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useStore from 'hooks/useStore';
import useThrottle from 'hooks/useThrottle';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { fontSize14Mixin, paragraph500Mixin } from 'styles';
import { numberUtils } from 'utils';

const CalculatorForm = ({
  information,
  countryConfig,
  isCalculatorFormAccordion = false,
  styleWrapper,
  styleWrapperBodyContent,
  styleAccordionButton,
  setFetching = () => {},
  isExpandDefault = false,
  ref,
  isForceCalculatorFormAccordionExpand,
  // Defaults true so callers that don't thread a settle signal keep current
  // behaviour; the add-transaction flow (Remittance/ver2) passes the real,
  // live value so the "applied" text/line is shown only while discount
  // validation is settled (not during apply/pending/fetch, including
  // re-validation after an amount change).
  isDiscountSettled = true,
}) => {
  const { t } = useLang();
  const { state } = useStore();
  const { addTransactionInformation } = state;
  const remittance = information || addTransactionInformation.remittance;
  const receiverPayoutMethod = addTransactionInformation.receiverPayoutMethod;
  const {
    currentCurrencySelected,
    currentCurrencySelectedAnother,
    isAddAnotherReceivingCurrency,
  } = remittance;
  const { isMobile, isTablet } = useDetectDevice();
  const { renderDiscountFieldLabel } = useAddTransaction();
  const { throttle } = useThrottle(1);
  const { isMakePayment } = useFormAddTransactionStore();

  const [originalFee, setOriginalFee] = useState(null);
  const { calculatorRoundingResponse, isCalculatorRounding } =
    useCalculatorRoundingStore();

  const { originalFee: originalFeeStore } = calculatorRoundingResponse || {};

  const paymentMethod =
    addTransactionInformation?.paymentMethod[
      ADD_TRANSACTION_PAYMENT_METHOD_NAME
    ];

  const discountApplied =
    addTransactionInformation?.paymentMethod?.discountApplied;
  const { discountAmount, totalAmountAfterDiscount, type, code } =
    discountApplied || {};
  const isDiscountApplied = Boolean(Number(discountAmount));

  const onlyCurrencySelectedByVND =
    !isAddAnotherReceivingCurrency &&
    currentCurrencySelected?.currencyCode === 'VND';

  const getBuyCurrencies = () => {
    if (!isAddAnotherReceivingCurrency)
      return [currentCurrencySelected?.currencyCode];
    return [
      currentCurrencySelected?.currencyCode,
      currentCurrencySelectedAnother?.currencyCode,
    ];
  };

  const parseAmountValue = (value) => {
    return value && format.toAmountFloatStr(numberUtils.mathRound(value));
  };

  const dmCode = receiverPayoutMethod[ADD_TRANSACTION_PAYOUT_METHOD_NAME];

  const sendAmount = numberUtils.mathRound(
    remittance[ADD_TRANSACTION_YOU_SEND_NAME] || 0,
  );
  const sendAnotherAmount = numberUtils.mathRound(
    remittance[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0,
  );
  const sendTotalAmount = numberUtils.mathRound(sendAmount + sendAnotherAmount);
  const isCalculatorRoundingBySendAmount =
    isCalculatorRounding && sendTotalAmount;

  const feeOriginAmount = numberUtils.mathRound(originalFee?.feeAmount || 0);

  const feeAmount = sendTotalAmount
    ? numberUtils.mathRound(remittance?.feeAmount?.feeAmount || 0)
    : 0;

  // DEV-1434: this component only ever backs live Create/Make-Other-Payment
  // flows, which always exclude the surcharge row. Surcharge is never a
  // persisted backend value here (only a live rate-based estimate), so it is
  // unconditionally hidden rather than computed and gated.
  const isShowSurchargeField = false;

  const totalPayAmount = isDiscountApplied
    ? Number(totalAmountAfterDiscount)
    : numberUtils.mathRound(feeAmount + sendTotalAmount);

  const discountPercent = useMemo(() => {
    return SENDING_AMOUNT_DISCOUNT_PERCENTS.find((o) => {
      if (o.range.length === 1 && sendTotalAmount >= o.range[0]) return o;

      if (
        feeAmount &&
        feeOriginAmount &&
        sendTotalAmount >= o.range[0] &&
        sendTotalAmount <= o.range[1]
      )
        return o;

      return null;
    });
  }, [sendTotalAmount, feeAmount, feeOriginAmount]);

  const isShowDiscountPercent = Boolean(
    onlyCurrencySelectedByVND &&
    Boolean(discountPercent) &&
    feeAmount &&
    feeOriginAmount &&
    feeOriginAmount !== feeAmount,
  );

  const isShowFeeDiscount = Boolean(
    sendTotalAmount &&
    feeAmount &&
    feeOriginAmount &&
    feeOriginAmount - feeAmount > 0,
  );

  const isShowFee = Boolean(sendTotalAmount && feeAmount);

  const fetchOriginalFee = async (
    countryCode,
    dmCode,
    sendAmount,
    isAddAnotherReceivingCurrency,
    pickupOfficeId,
  ) => {
    throttle(async () => {
      try {
        const { data } = await api.lookupOriginalFee(
          countryCode,
          dmCode,
          sendAmount,
          isAddAnotherReceivingCurrency
            ? 'AUD'
            : currentCurrencySelected?.currencyCode,
          pickupOfficeId,
        );

        if (data) {
          setOriginalFee(data);
        }

        setFetching(false);
      } catch (error) {
        checkMaintenance(error);
        console.error(
          `🔥 CalculatorForm ❌ fetchOriginalFee ➡️ ERROR ↩`,
          error?.message,
        );
        setFetching(false);
      }
    });
  };
  useEffect(() => {
    if (!sendTotalAmount) {
      setOriginalFee(null);
      return;
    }
    if (sendTotalAmount) {
      if (isMakePayment && isEmpty(originalFeeStore)) {
        fetchOriginalFee(
          countryConfig?.countryCode,
          dmCode,
          sendTotalAmount,
          isAddAnotherReceivingCurrency,
          currentCurrencySelected?.pickupOfficeId,
        );
      } else {
        setOriginalFee(originalFeeStore);
        setFetching(false);
      }
    }
  }, [
    isMakePayment,
    sendTotalAmount,
    currentCurrencySelected,
    isAddAnotherReceivingCurrency,
    currentCurrencySelectedAnother,
    originalFeeStore,
  ]);

  const renderTotalLabel = () => {
    if (!paymentMethod) return t('label_sub_total');

    return t('label_total_to_pay');
  };

  const renderSendingAmountValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorFieldValue>
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    return (
      <CalculatorFieldValue>{`${parseAmountValue(
        sendTotalAmount,
      )} AUD`}</CalculatorFieldValue>
    );
  };

  const renderFeeAmountValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorFieldValue>
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    return (
      <CalculatorFieldValue>{`${parseAmountValue(
        feeOriginAmount,
      )} AUD`}</CalculatorFieldValue>
    );
  };

  const renderDiscountAmountValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorFieldValue>
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </CalculatorFieldValue>
      );
    return (
      <CalculatorFieldValue className="fee-value">
        -{`${parseAmountValue(feeOriginAmount - feeAmount)} AUD`}
      </CalculatorFieldValue>
    );
  };
  const renderDiscountSection = () => {
    if (!isShowFeeDiscount) return null;
    return (
      <CalculatorFieldWrap>
        <CalculatorFieldLabel className="label-percent">
          {t('label_discounted_amount')}{' '}
          {isShowDiscountPercent && discountPercent && !isCalculatorRounding ? (
            <>
              {isMobile || isTablet ? (
                <TooltipCommonMobile
                  name="add-transaction-calculator-form"
                  text={t(discountPercent.tooltip)}
                  icon={discountPercent.imgSrc}
                  percentValue={discountPercent.percent}
                  isUseImage={false}
                />
              ) : (
                <TooltipCommon
                  text={t(discountPercent.tooltip)}
                  icon={discountPercent.imgSrc}
                  percentValue={discountPercent.percent}
                  isPopover
                  isDangerouslySetInnerHTML={true}
                  isUseImage={false}
                />
              )}
            </>
          ) : (
            <></>
          )}
        </CalculatorFieldLabel>
        {renderDiscountAmountValue()}
      </CalculatorFieldWrap>
    );
  };

  const renderDiscountApplied = () => {
    if (!isDiscountApplied || !isDiscountSettled || isCalculatorRounding)
      return null;
    return (
      <CalculatorFieldWrap>
        <CalculatorFieldLabel
          dangerouslySetInnerHTML={{
            __html: renderDiscountFieldLabel(type, code),
          }}
        />
        <CalculatorFieldValue>
          -{`${parseAmountValue(Number(discountAmount))} AUD`}
        </CalculatorFieldValue>
      </CalculatorFieldWrap>
    );
  };

  const renderSurchargeField = () => {
    if (!isShowSurchargeField) return null;
    return (
      <CalculatorFieldWrap>
        <CalculatorFieldLabel>
          {t('label_surcharge_label')} <span>{'(1.2%)'}</span>
        </CalculatorFieldLabel>
      </CalculatorFieldWrap>
    );
  };

  const renderTotalToPayValue = () => {
    if (isCalculatorRoundingBySendAmount)
      return (
        <CalculatorTotalValue>
          <CalculatorRoundingAnimationLottie width="37px" height="37px" />
        </CalculatorTotalValue>
      );
    return (
      <CalculatorTotalValue>{`AUD ${parseAmountValue(
        totalPayAmount,
      )}`}</CalculatorTotalValue>
    );
  };

  const renderDiscountAppliedText = () => {
    if (!isDiscountApplied || !isDiscountSettled) return '';
    return (
      <DiscountAppliedText>{t('label_voucher_applied')}</DiscountAppliedText>
    );
  };

  if (isCalculatorFormAccordion) {
    return (
      <CustomCalculatorFormAccordion
        totalPayAmount={totalPayAmount}
        sendAmount={sendTotalAmount}
        feeAmount={feeAmount}
        isShowDiscountPercent={isShowDiscountPercent}
        discountPercent={discountPercent}
        originalFee={feeOriginAmount}
        isShowSurchargeField={isShowSurchargeField}
        isDiscountApplied={isDiscountApplied}
        isDiscountSettled={isDiscountSettled}
        discountAmount={discountAmount}
        isExpandDefault={isExpandDefault}
        styleWrapper={styleWrapper}
        styleWrapperBodyContent={styleWrapperBodyContent}
        styleAccordionButton={styleAccordionButton}
        paymentMethod={paymentMethod}
        discountApplied={discountApplied}
        isShowFeeDiscount={isShowFeeDiscount}
        isShowFee={isShowFee}
        isForceCalculatorFormAccordionExpand={
          isForceCalculatorFormAccordionExpand
        }
      />
    );
  }
  return (
    <CalculatorFormStyled>
      <CalculatorDetails ref={ref}>
        <Label>{t('label_transaction_details')}</Label>
        <CalculatorFieldWrap>
          <CalculatorFieldLabel>
            {t('label_sending_amount')}
          </CalculatorFieldLabel>
          {renderSendingAmountValue()}
        </CalculatorFieldWrap>
        {isShowFee && (
          <CalculatorFieldWrap>
            <CalculatorFieldLabel>{t('label_fee')}</CalculatorFieldLabel>
            {renderFeeAmountValue()}
          </CalculatorFieldWrap>
        )}
        {renderDiscountSection()}
        {renderDiscountApplied()}
        {renderSurchargeField()}
      </CalculatorDetails>
      <CalculatorTotal>
        <CalculatorFieldWrap
          style={{
            height: '64px',
            // borderBottom: '1px solid #ffffff',
            marginBottom: '13px',
            flexDirection: 'column',
            alignItems: 'end',
          }}
        >
          <CalculatorTotalLabel>{renderTotalLabel()}</CalculatorTotalLabel>
          {renderTotalToPayValue()}
          {renderDiscountAppliedText()}
        </CalculatorFieldWrap>
        {/* <CalculatorFieldWrap>
          <Label
            style={{
              color: '#ffffff',
              marginBottom: '0px',
            }}
          >
            {t('label_receiving_currency')}
          </Label>
          <CurrenciesWrap>
            {getBuyCurrencies()?.map((currencyCode) => (
              <CurrencyChip key={currencyCode}>{currencyCode}</CurrencyChip>
            ))}
          </CurrenciesWrap>
        </CalculatorFieldWrap> */}
      </CalculatorTotal>
    </CalculatorFormStyled>
  );
};

const CalculatorFormStyled = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
`;

const CalculatorDetails = styled.div`
  margin-top: 0px;
  min-width: 437px;
  width: 437px;
  background: var(--ds-bg-2);
  border-radius: 8px;
  padding: 10px 16px;
`;
const Label = styled.p`
  margin: 0;
  margin-bottom: 10px;
  padding: 0;

  font-family: var(--ff-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  text-align: left;
  color: var(--ds-c-grey-dark);
  text-transform: uppercase;
`;
const CalculatorFieldWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 30px;
  height: fit-content;
  margin-bottom: 0px;
  border-bottom: 1px dashed #979797;

  &:last-child {
    border-bottom: none;
  }
`;
const CalculatorFieldLabel = styled.p`
  ${paragraph500Mixin};
  ${fontSize14Mixin};
  font-weight: 400;

  margin: 0;
  padding: 0;

  & span {
    &.discount-code {
      color: var(--ds-c-green-default);
    }
  }

  &.label-percent {
    display: flex;
    align-items: center;
  }
`;
const CalculatorFieldValue = styled.p`
  ${paragraph500Mixin};
  ${fontSize14Mixin};
  font-weight: 400;
  text-transform: uppercase;

  &.fee-value {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &.summary-discount {
    color: #b0b6bd;
    display: flex;
    align-items: center;
  }

  & span {
    font-size: 12px;
    line-height: 14px;
    color: #ffffff;

    background-color: var(--ds-c-green-default);
    padding: 2px 8px;
    border-radius: 2px;
    margin-right: 8px;
  }

  & b {
    font-weight: 500;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    & span {
      font-size: 14px;
      line-height: 17px;
    }
  }
`;

const CalculatorTotal = styled.div`
  margin-top: 0px;
  width: 100%;
  background: var(--ds-bg-3);
  border-radius: 8px;
  padding: 10px 16px;
  display: grid;
  align-content: center;
`;
const CalculatorTotalLabel = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  text-transform: uppercase;

  margin: 0;
  padding: 0;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const CalculatorTotalValue = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 600;
  font-size: 32px;
  line-height: 40px;
  text-transform: uppercase;
  color: #ffffff;

  margin: 0;
  padding: 0;
`;

const CurrenciesWrap = styled.div`
  display: flex;
  gap: 4px;
`;
const CurrencyChip = styled.span`
  height: 20px;
  padding-inline: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--ds-c-blue-dark);
  border-radius: 100px;

  font-family: var(--ff-primary);
  font-size: 12px;
  font-weight: 600;
  line-height: 15px;
  color: #fff;
`;

const DiscountAppliedText = styled.p`
  ${paragraph500Mixin};
  color: #33f268;
`;

export default CalculatorForm;
